import type { ProductType, Prompt } from "@1js/pl-card-components";
import { isExtensionPrompt } from "@1js/pl-types";
import * as React from "react";
import UAParser from "ua-parser-js";
import { PropertyContext } from "../pages/PropertyContext.web";
import { handoffToAugloop } from "../services/handoffAugloop";
import { handoffToM365Chat } from "../services/handoffToM365Chat";
import type { HandoffFile } from "../services/workingsetfiles-client.web";
import { type SiteSetting } from "../types";
import useTelemetry from "../utils/Telemetry/useTelemetry";
import {
  type HandoffCallback,
  type HandoffCallbackResult,
  type HandoffResult,
} from "./handoff.types";
import { useFeatureEnablement } from "./useFeatureEnablement";
import { useFlightContext } from "./useFlightContext";

type ProductMappingType = Map<
  ProductType | string,
  {
    requirements: (settings: SiteSetting, prompt?: Prompt) => boolean;
    method: (
      prompt: Prompt,
      product: ProductType | string,
      host: string,
      locale: string,
      file: HandoffFile | undefined,
      abortController: AbortController
    ) => Promise<HandoffCallbackResult>;
  }
>;

/**
 * Orchestrates handoff of prompts to different products and services, like M365 Chat and Office AI (Word, Excel, PowerPoint, OneNote, Outlook), to enable customers to try prompts in those products and services.
 * @returns methods to handoff prompts to different products and services
 */
export const useHandoffService = (): HandoffResult => {
  const [siteSetting] = React.useContext(PropertyContext);
  const { isFlightActive } = useFlightContext();
  const { device } = new UAParser().getResult();
  const { includesAny } = useFeatureEnablement();
  const telemetry = useTelemetry();

  const isMobile = React.useCallback(() => {
    return (
      device.type === "mobile" ||
      device.type === "tablet" ||
      device.type === "wearable"
    );
  }, [device.type]);

  const productsInFlight = React.useMemo(() => {
    const products = [];
    if (isFlightActive("isTryInWordEnabled")) {
      products.push("Word");
    }

    if (isFlightActive("isTryInExcelEnabled")) {
      products.push("Excel");
    }

    if (isFlightActive("isTryInPowerPointEnabled")) {
      products.push("PowerPoint");
    }

    return products;
  }, [isFlightActive]);

  const productMapping: ProductMappingType = React.useMemo(() => {
    const innerMap: ProductMappingType = new Map();
    innerMap.set("BizChat", {
      requirements: (settings: SiteSetting, prompt?: Prompt) =>
        (isFlightActive("isTryInBizChatEnabled") &&
          ((isFlightActive("isTryInDisableCosmosDBEnabled") &&
            prompt?.Repository !== "CosmosDB") ||
            !isFlightActive("isTryInDisableCosmosDBEnabled")) &&
          !isMobile() &&
          includesAny(settings.licenses, ["CopilotBusinessChat", "Copilot"]) &&
          (!isFlightActive("isTryIn1_1FormatEnabled") ||
            (isFlightActive("isTryIn1_1FormatEnabled") &&
              prompt?.Origin !== "User") ||
            isFlightActive("isTryIn3SPromptLinkEnabled"))) ||
        false,
      method: async (prompt: Prompt) =>
        await handoffToM365Chat(
          prompt,
          "M365Chat",
          isFlightActive("isTryInAppendM365ChatFeatures"),
          isFlightActive("isTryIn1_1FormatEnabled"),
          isFlightActive("isTryIn3SPromptLinkEnabled"),
          telemetry.sendApiActionEvent
        ),
    });
    productsInFlight.forEach((product) => {
      innerMap.set(product, {
        requirements: (settings: SiteSetting) =>
          (includesAny(settings.licenses, ["OfficeProPlus"]) &&
            isFlightActive("isTryInOfficeEnabled")) ||
          false,
        method: async (
          prompt: Prompt,
          product: string,
          host: string,
          locale: string,
          file: HandoffFile | undefined,
          abortController: AbortController
        ) =>
          await handoffToAugloop(
            host,
            locale,
            prompt,
            product,
            file,
            abortController
          ),
      });
    });
    return innerMap;
  }, [includesAny, isFlightActive, isMobile, productsInFlight]);

  /**
   * Method to handoff the prompt to the appropriate product or service
   * @param props.prompt - The prompt to handoff
   * @param props.destination - The destination product or service to handoff the prompt to
   * @param props.host - The host of SupportService
   * @param props.locale - The locale of the user
   * @returns handoffUrl to direct customer to in browser, or error if handoff failed
   */
  const onHandoff = async (
    props: HandoffCallback
  ): Promise<HandoffCallbackResult> => {
    const { prompt, destination, host, locale, file, abortController } = props;
    if (!productMapping.has(destination)) {
      throw new Error("UnknownDestination");
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const handoffMethod = productMapping.get(destination)!;

    return await handoffMethod.method(
      prompt,
      destination,
      host,
      locale,
      file,
      abortController
    );
  };

  const isHandoffSupported = React.useCallback(
    (product: ProductType | string, prompt?: Prompt): boolean => {
      return (
        (!isExtensionPrompt(prompt as Prompt) &&
          productMapping.has(product) &&
          productMapping.get(product)?.requirements(siteSetting, prompt)) ||
        false
      );
    },
    [productMapping, siteSetting]
  );

  const isProductNeedsFile = (product: ProductType | string) => {
    if (!productMapping.has(product)) {
      return false;
    }

    return !["BizChat", "Teams", "Topics"].includes(product);
  };

  return {
    handoff: onHandoff,
    isHandoffSupported,
    isProductNeedsFile,
  };
};
